import { useState } from "react";
import { ArrowDown, ArrowUp, ChevronsUp, Plus, Trash2 } from "lucide-react";
import { Boton, Campo, Input, Select } from "@/components/ui";
import { Chips, Dinero, Stepper, Tiles } from "@/components/ui/controles";
import { useToast } from "@/components/ui/Toast";
import { Dumbbell, BUCKET_ICONO } from "@/components/charts";
import { fmtPct } from "@/engine/format/esMX";
import type { ObjectiveInput, ObjectiveRow, Snapshot } from "@/engine/types";
import { useDatos } from "../store/store";
import { agregarObjetivo, moverObjetivo, objetivosOrdenados, quitarObjetivo, setFila, setLife } from "../store/acciones";
import { ESTRATEGIAS, MESES_FONDO, P } from "../catalogo-ui";
import { dinero } from "../util";
import { SeccionLayout, Bloque } from "../componentes/SeccionLayout";
import { Ejemplo } from "../componentes/Ejemplo";
import { PanelEnVivo } from "../componentes/PanelEnVivo";
import { useFotoEnVivo } from "../useFotoEnVivo";
import * as A from "@/features/foto/adaptadores";

const GASTO = "Gasto único", VIVIR = "Vivir de mi portafolio de inversión";

function CalculadoraCapital({ o, cur }: { o: ObjectiveInput; cur: string }) {
  const apply = useDatos((x) => x.apply);
  const tasa = P.safeWithdrawalRate;
  const mensual = o.costToday === null ? null : Math.round((o.costToday * tasa) / 12);
  const [directo, setDirecto] = useState(false);
  return (
    <div className="flex flex-col gap-2">
      {!directo ? (
        <>
          <Campo etiqueta="¿Cuánto quieres gastar al mes, en pesos de hoy?" htmlFor={`m${o.row}`}>
            <Dinero id={`m${o.row}`} valor={mensual} moneda={cur} onChange={(v) => apply(setFila("objectives", o.row, { costToday: v === null ? null : Math.round((v * 12) / tasa) }))} />
          </Campo>
          {o.costToday !== null && <p className="rounded-md bg-fondo p-3 text-sm">Capital necesario: <b className="tabular">{dinero(o.costToday, cur)}</b><br /><span className="text-xs text-tinta-suave">= {dinero(mensual, cur)} × 12 ÷ {fmtPct(tasa, 0)}. Es lo que tendrías que tener invertido para vivir de él (25 años de gasto).</span></p>}
        </>
      ) : (
        <Campo etiqueta="Capital que necesitas (pesos de hoy)" htmlFor={`c${o.row}`}><Dinero id={`c${o.row}`} valor={o.costToday} moneda={cur} onChange={(v) => apply(setFila("objectives", o.row, { costToday: v }))} /></Campo>
      )}
      <button type="button" className="self-start text-xs font-medium text-primary underline" onClick={() => setDirecto((v) => !v)}>{directo ? "Calcularlo con lo que quiero gastar al mes" : "Ya sé el capital, lo escribo directo"}</button>
    </div>
  );
}

function VistaEnVivo({ r, cur }: { r: ObjectiveRow | undefined; cur: string }) {
  if (!r || r.setAsideToday === null) return <p className="text-xs text-tinta-suave">Completa edad, tipo y costo para ver cuánto apartar hoy.</p>;
  const pct = r.fundedPct ?? 0;
  return (
    <div className="rounded-md bg-fondo p-3 text-sm">
      <p className="text-tinta-suave">Plazo <b className="text-tinta">{r.years} años</b> · estrategia <b className="text-tinta">{r.strategyFinal}</b>{r.costThen !== null && <> · ese año costará <b className="tabular text-tinta">{dinero(r.costThen, cur)}</b></>}</p>
      <p className="mt-1">Apartar hoy: <b className="tabular">{dinero(r.setAsideToday, cur)}</b> · financiado <b className="tabular">{fmtPct(pct, 0)}</b>{(r.missing ?? 0) > 0.5 && <> · faltan <b className="tabular">{dinero(r.missing, cur)}</b></>}</p>
      {pct < 0.005 && (r.order ?? 0) > 1 && <p className="mt-1 text-xs text-tinta-suave">0% no es un error: tu capital se reparte en orden y se acabó antes de llegar a este.</p>}
    </div>
  );
}

function TarjetaObjetivo({ o, pos, total, snap, cur, edad }: { o: ObjectiveInput; pos: number; total: number; snap: Snapshot | null; cur: string; edad: number | null }) {
  const apply = useDatos((x) => x.apply);
  const r = snap?.life.objectives.find((x) => !x.isEmergencyFund && x.row === o.row);
  const tipo = o.type === VIVIR ? VIVIR : o.type === GASTO ? GASTO : null;
  return (
    <li id={`objetivo-${o.row}`} className="scroll-mt-28 rounded-md border border-borde bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-borde px-3 py-2">
        <span className="text-sm font-semibold text-tinta-suave">Prioridad {pos}</span>
        <span className="flex">
          <button type="button" aria-label="Subir prioridad" disabled={pos === 1} onClick={() => apply(moverObjetivo(o.row, -1))} className="rounded p-2.5 text-tinta-suave hover:bg-gray-100 disabled:opacity-30"><ArrowUp className="h-4 w-4" aria-hidden /></button>
          <button type="button" aria-label="Bajar prioridad" disabled={pos === total} onClick={() => apply(moverObjetivo(o.row, 1))} className="rounded p-2.5 text-tinta-suave hover:bg-gray-100 disabled:opacity-30"><ArrowDown className="h-4 w-4" aria-hidden /></button>
          {pos > 2 && <button type="button" aria-label="Mover al primer lugar" onClick={() => apply(moverObjetivo(o.row, -99))} className="rounded p-2.5 text-tinta-suave hover:bg-gray-100"><ChevronsUp className="h-4 w-4" aria-hidden /></button>}
          <button type="button" aria-label={`Quitar ${o.name}`} onClick={() => apply(quitarObjetivo(o.row))} className="rounded p-2.5 text-tinta-suave hover:bg-gray-100"><Trash2 className="h-4 w-4" aria-hidden /></button>
        </span>
      </div>
      <div className="flex flex-col gap-4 p-4">
        <Campo etiqueta="¿Cuál es el objetivo?" htmlFor={`n${o.row}`}><Input id={`n${o.row}`} value={o.name ?? ""} onChange={(e) => apply(setFila("objectives", o.row, { name: e.target.value || "Objetivo" }))} placeholder="Ej.: Universidad de los niños" /></Campo>
        <div>
          <p className="mb-2 text-sm font-medium">¿De qué tipo es?</p>
          <Tiles etiqueta="Tipo de objetivo" valor={tipo} onChange={(v) => apply(setFila("objectives", o.row, { type: v }))} opciones={[
            { valor: GASTO, titulo: "Gasto único", linea: "Algo que pagas una vez: casa, universidad, boda, remodelación." },
            { valor: VIVIR, titulo: "Vivir de mi portafolio", linea: "Dejar de depender de tu trabajo. Aquí el costo es el capital que lo sostiene." },
          ]} />
        </div>
        <Campo etiqueta="¿A qué edad?" htmlFor={`e${o.row}`} error={o.age !== null && o.age > 120 ? "Pon tu edad en ese momento, no el año." : undefined}
          ayuda={o.age !== null && edad !== null && o.age <= 120 ? (o.age <= edad ? "Ya es ahora: plazo 0 años." : `En ${o.age - edad} años.`) : undefined}>
          <Stepper id={`e${o.row}`} valor={o.age} min={edad ?? 18} max={110} sufijo="años" onChange={(v) => apply(setFila("objectives", o.row, { age: v }))} />
        </Campo>
        {tipo === VIVIR ? <><CalculadoraCapital o={o} cur={cur} /><Ejemplo campo="objetivos.libertad" /></> : (
          <Campo etiqueta="¿Cuánto cuesta en pesos de HOY?" htmlFor={`f${o.row}`} ayuda="No lo infles por inflación: el modelo lo hace solo.">
            <Dinero id={`f${o.row}`} valor={o.costToday} moneda={cur} onChange={(v) => apply(setFila("objectives", o.row, { costToday: v }))} />
          </Campo>
        )}
        <details className="text-sm">
          <summary className="cursor-pointer text-tinta-suave">Cambiar la estrategia (opcional){r?.strategySuggested ? ` · sugerida: ${r.strategySuggested}` : ""}</summary>
          <p className="mt-2 text-xs text-tinta-suave">La estrategia sale del plazo, no de tu tolerancia al riesgo. Déjala vacía la primera vez.</p>
          <Select className="mt-2" value={o.strategyOverride ?? ""} onChange={(e) => apply(setFila("objectives", o.row, { strategyOverride: e.target.value || null }))} aria-label="Estrategia">
            <option value="">Dejar la sugerida por el plazo</option>{ESTRATEGIAS.map((x) => <option key={x} value={x}>{x}</option>)}
          </Select>
        </details>
        <VistaEnVivo r={r} cur={cur} />
      </div>
    </li>
  );
}

export function Objetivos() {
  const inputs = useDatos((x) => x.inputs);
  const apply = useDatos((x) => x.apply);
  const toast = useToast();
  const { snapshot } = useFotoEnVivo();
  const cur = inputs.setup.currency === "USD" ? "USD" : "MXN";
  const obs = objetivosOrdenados(inputs);
  const meses = inputs.life.emergencyMonths;
  const montoFondo = snapshot?.life.emergency.amount ?? null;
  const agregar = () => {
    const r = agregarObjetivo(useDatos.getState().inputs, { name: "Nuevo objetivo", age: inputs.setup.age !== null ? inputs.setup.age + 10 : null });
    if (r.row === null) { toast.error(`El modelo tiene lugar para ${inputs.life.objectives.length} objetivos.`); return; }
    apply(() => r.inputs);
    setTimeout(() => document.getElementById(`objetivo-${r.row}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };
  const al = snapshot ? A.allocation(snapshot) : null;
  const casc = snapshot ? A.cascada(snapshot) : null;
  return (
    <div className="grid gap-6 md:grid-cols-[7fr_5fr]">
      <SeccionLayout id="objetivos" intro="Primero tu fondo de emergencia; luego tus objetivos en orden de prioridad. Tu capital de hoy se reparte en ese orden.">
        <Bloque id="fondo" titulo="Fondo de emergencia" ayuda="Es fijo y va siempre primero. Solo eliges cuántos meses de gastos te dejan dormir; el monto sale de tus gastos.">
          <Chips etiqueta="Meses del fondo" valor={MESES_FONDO.some((m) => m.valor === meses) ? meses : null} onChange={(v) => apply(setLife({ emergencyMonths: v }))} opciones={MESES_FONDO.map((m) => ({ valor: m.valor, etiqueta: `${m.etiqueta} · ${m.ayuda}` }))} />
          <div className="mt-3 max-w-xs"><Stepper valor={meses} min={1} max={60} sufijo="meses" ariaLabel="Meses del fondo" onChange={(v) => apply(setLife({ emergencyMonths: v }))} /></div>
          <p className="mt-2 text-sm text-tinta-suave">{montoFondo !== null ? <>= {meses} meses × tu gasto mensual = <b className="tabular text-tinta">{dinero(montoFondo, cur)}</b></> : "Captura tus gastos para ver el monto."}</p>
          <div className="mt-2"><Ejemplo campo="fondo" /></div>
        </Bloque>
        <Bloque titulo="Tus objetivos" ayuda="En pesos de hoy. El orden de la lista es tu prioridad: el de arriba recibe capital primero." accion={<Boton tamano="sm" onClick={agregar}><Plus className="h-4 w-4" aria-hidden /> Agregar</Boton>}>
          <Ejemplo campo="objetivos" />
          {obs.length === 0 ? (
            <button type="button" onClick={agregar} className="mt-3 flex w-full min-h-20 flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-borde p-4 text-tinta-suave hover:border-primary">
              <Plus className="h-6 w-6" aria-hidden /><span className="font-medium text-tinta">Agregar mi primer objetivo</span><span className="text-sm">Libertad financiera, universidad, casa, remodelación…</span>
            </button>
          ) : <ol className="mt-3 flex flex-col gap-3">{obs.map((o, k) => <TarjetaObjetivo key={o.row} o={o} pos={k + 1} total={obs.length} snap={snapshot} cur={cur} edad={inputs.setup.age} />)}</ol>}
          {obs.length > 1 && <div className="mt-3"><Ejemplo campo="objetivos.prioridad" /></div>}
        </Bloque>
        {casc && casc.estado !== "vacio" && (
          <div className="rounded-md border border-borde bg-white p-4 text-sm">
            <p>{casc.lectura}</p>
            {al && al.estado !== "vacio" && (
              <div className="mt-3"><p className="mb-2 font-medium">Tu allocation objetivo sale de estos plazos</p>
                <Dumbbell filas={al.datos.filas.map((x) => ({ etiqueta: `${BUCKET_ICONO[x.bucket]} ${x.bucket}`, a: x.hoy, b: x.objetivo, banda: x.banda }))} dominio={[0, 1]} etiquetaA="Hoy" etiquetaB="Objetivo" formato={(v) => fmtPct(v, 0)} /></div>
            )}
          </div>
        )}
        <Bloque titulo="Capital libre (avanzado)" ayuda="Si te sobra capital después de financiar todos tus objetivos, con qué estrategia se invierte.">
          <Select value={inputs.life.freeCapitalStrategy ?? ""} onChange={(e) => apply(setLife({ freeCapitalStrategy: e.target.value || null }))} aria-label="Estrategia del capital libre">
            {ESTRATEGIAS.map((x) => <option key={x} value={x}>{x}</option>)}
          </Select>
        </Bloque>
      </SeccionLayout>
      <PanelEnVivo />
    </div>
  );
}
